import { DateTime } from 'luxon';
import { FormEvent, useState } from 'react';

import { setData } from '../utils/account';
import { Stats, useStats } from '../utils/stats';

const PASSWORD = 'goldstandard';

export function ChangeStatsRoute() {
  const [stats, setStats] = useStats();
  const [authenticated, setAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [error, setError] = useState('');

  const [currentStreak, setCurrentStreak] = useState(stats.currentStreak);
  const [maxStreak, setMaxStreak] = useState(stats.maxStreak);
  const [played, setPlayed] = useState(stats.played);
  const [winCount, setWinCount] = useState(stats.winCount);
  const [guessDistribution, setGuessDistribution] = useState(() => {
    const dist = stats.guessDistribution;
    return Array.from({ length: 6 }, (_, i) => dist[i] ?? 0);
  });
  const [saved, setSaved] = useState(false);

  const handleLogin = (e: FormEvent) => {
    e.preventDefault();
    if (passwordInput === PASSWORD) {
      setAuthenticated(true);
      setError('');
    } else {
      setError('Incorrect password');
    }
  };

  const handleSave = async () => {
    const distArray = guessDistribution.map((v) => {
      const n = Math.floor(Number(v));
      return Number.isFinite(n) ? n : 0;
    });

    const finalMaxStreak = Math.max(Number(currentStreak), Number(maxStreak));

    const newStats: Stats = {
      currentStreak: Number(currentStreak),
      maxStreak: finalMaxStreak,
      played: Number(played),
      winCount: Number(winCount),
      guessDistribution: distArray,
      previousDate: DateTime.now(),
    };

    setMaxStreak(finalMaxStreak);
    setStats(newStats);
    setData('stats', newStats);
    setSaved(true);
    setError('');
    setTimeout(() => setSaved(false), 2000);
  };

  if (!authenticated) {
    return (
      <div className="flex flex-col items-center gap-4 p-6 w-full max-w-lg">
        <h2 className="text-xl font-bold">Change Stats</h2>
        <form onSubmit={handleLogin} className="flex flex-col gap-3 w-full">
          <label className="text-sm font-medium">Password</label>
          <input
            type="password"
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            className="border rounded px-3 py-2 dark:bg-slate-800 dark:border-slate-600"
          />
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <button
            type="submit"
            className="bg-blue-600 text-white rounded px-4 py-2 hover:bg-blue-700"
          >
            Unlock
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4 p-6 w-full max-w-lg">
      <h2 className="text-xl font-bold">Change Stats</h2>

      <div className="flex flex-col gap-3 w-full">
        <Field
          label="Current Streak"
          value={currentStreak}
          onChange={setCurrentStreak}
          type="number"
        />
        <Field
          label="Max Streak"
          value={maxStreak}
          onChange={setMaxStreak}
          type="number"
        />
        <Field
          label="Played"
          value={played}
          onChange={setPlayed}
          type="number"
        />
        <Field
          label="Win Count"
          value={winCount}
          onChange={setWinCount}
          type="number"
        />
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Guess Distribution</label>
          <div className="grid grid-cols-6 gap-2">
            {guessDistribution.map((val, i) => (
              <div key={i} className="flex flex-col items-center">
                <span className="text-xs text-gray-500 mb-1">{i + 1}</span>
                <input
                  type="number"
                  value={val}
                  onChange={(e) => {
                    const updated = [...guessDistribution];
                    updated[i] = Number(e.target.value);
                    setGuessDistribution(updated);
                  }}
                  className="border rounded px-2 py-2 w-full text-center dark:bg-slate-800 dark:border-slate-600"
                />
              </div>
            ))}
          </div>
        </div>
        {error && <p className="text-red-500 text-sm">{error}</p>}
        {saved && <p className="text-green-500 text-sm">Saved!</p>}

        <button
          onClick={handleSave}
          className="bg-green-600 text-white rounded px-4 py-2 hover:bg-green-700 mt-2"
        >
          Save Stats
        </button>
      </div>
    </div>
  );
}

function Field<T extends string | number>({
  label,
  value,
  onChange,
  type = 'text',
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  type?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) =>
          onChange(
            (type === 'number' ? Number(e.target.value) : e.target.value) as T,
          )
        }
        className="border rounded px-3 py-2 dark:bg-slate-800 dark:border-slate-600"
      />
    </div>
  );
}
